// Small shared presentational helpers, forked from umbrage-client-pages/portal/web/src/ui.tsx.
// Restyled to the Umbrage token layer in src/styles.css — see that file for the single
// source of truth on colors, radii, shadows, and motion. APIs below are unchanged so
// screens don't need to be touched by this pass.
import { Fragment, useEffect, useRef, type ButtonHTMLAttributes, type ReactNode, type Ref } from 'react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import type { PageInfo, RecordingStatus } from '@scribe/core/types';
import type { UploadItem } from '@scribe/core/upload-queue';
import { useElapsedLabel, type InProgressPhase } from '../hooks/useElapsedLabel';
import { PLAYBACK_RATES, type PlaybackRate } from '../hooks/usePlaybackRate';

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';

/** Traps Tab/Shift+Tab focus within `ref` while `active`, closes on Escape, and restores
 * focus to whatever was focused before opening once `active` goes false. Shared by
 * AppShell's drawer/sheet and the Modal primitive below so every overlay in the app gets
 * identical a11y behavior. */
export function useFocusTrap(active: boolean, ref: React.RefObject<HTMLElement>, onClose: () => void) {
  const previouslyFocused = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!active) return;
    previouslyFocused.current = document.activeElement as HTMLElement | null;
    const node = ref.current;
    const focusable = node?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
    focusable?.[0]?.focus();

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key !== 'Tab' || !node) return;
      const items = Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
        (el) => el.offsetParent !== null,
      );
      if (items.length === 0) return;
      const first = items[0]!;
      const last = items[items.length - 1]!;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      previouslyFocused.current?.focus();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);
}

/** Shared modal/dialog shell: traps focus, closes on Escape and backdrop click, and
 * becomes a full-screen sheet on mobile (see .modal-overlay/.modal in styles.css). Always
 * mounted while shown (no internal `open` prop) — callers conditionally render it, matching
 * the existing screens' pattern of `{show && <Modal>...}`. */
export function Modal({
  children,
  onClose,
  wide = false,
  labelledBy,
  className = '',
}: {
  children: ReactNode;
  onClose: () => void;
  wide?: boolean;
  labelledBy?: string;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null!);
  useFocusTrap(true, ref, onClose);

  return (
    <div
      className="modal-overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={'modal' + (wide ? ' modal-wide' : '') + (className ? ' ' + className : '')}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        ref={ref}
      >
        {children}
      </div>
    </div>
  );
}

export function Badge({ kind, children }: { kind: string; children: ReactNode }) {
  return <span className={'badge badge-' + kind}>{children}</span>;
}

/** Primary/ghost button primitive on top of the plain <button>/.ghost classes already
 * used throughout the app — prefer this in new code; existing `<button className="ghost">`
 * usages keep working unchanged. */
export function Button({
  variant = 'primary',
  size,
  className = '',
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'ghost' | 'danger';
  size?: 'sm';
}) {
  const variantClass = variant === 'primary' ? '' : variant;
  const sizeClass = size ? ' ' + size : '';
  return <button className={[variantClass, className].filter(Boolean).join(' ') + sizeClass} {...props} />;
}

/** Icon-only button — always requires an aria-label since there's no visible text. */
export function IconButton({
  label,
  className = '',
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { label: string }) {
  return <button type="button" aria-label={label} className={'icon-btn ' + className} {...props} />;
}

export function Card({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <div className={'card ' + className}>{children}</div>;
}

export function Tag({ children }: { children: ReactNode }) {
  return <span className="tag">{children}</span>;
}

const STATUS_CHIP_LABEL: Record<'done' | 'prog' | 'compared' | 'failed', string> = {
  done: 'Transcribed',
  prog: 'Transcribing',
  compared: 'Compared vs Otter',
  failed: 'Failed',
};

/** Feed-card status chip with a leading color dot — the four semantic variants from the
 * mockup (done=Jungle, prog=Savoy, compared=Plum, failed=Rose). */
export function StatusChip({
  variant,
  children,
}: {
  variant: 'done' | 'prog' | 'compared' | 'failed';
  children?: ReactNode;
}) {
  return (
    <span className={`status-chip status-${variant}`}>
      {variant !== 'compared' && <span className="status-dot" aria-hidden="true" />}
      {children ?? STATUS_CHIP_LABEL[variant]}
    </span>
  );
}

/** Minimal spinning-ring glyph (Heroicons has no built-in spinner) for "still working, no
 * known progress" states — web-ui-issues #16. Deliberately NOT a percent/progress bar: AWS
 * Transcribe batch jobs report no completion fraction, so a spinner is the honest affordance
 * (something is happening) rather than a fabricated number. Animated via the `.spinner`
 * keyframe in styles.css, which itself respects `prefers-reduced-motion`. */
export function Spinner({ size = 14 }: { size?: number }) {
  return (
    <svg
      className="spinner"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
      <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

const IN_PROGRESS_STATUS_LABEL: Partial<Record<InProgressPhase, string>> = {
  queued: 'Queued',
  transcribing: 'Transcribing…',
  // §2.12: two states that both read `status === 'transcribing'` but mean different things to
  // a user. Neither gets an elapsed readout — nothing records when capture ended, so any
  // number here would be invented (see `inProgressSince`).
  capturing: 'Recording…',
  finalizing: 'Finalizing audio…',
};

/**
 * Honest, non-percent status treatment for a recording genuinely mid-pipeline (`queued` /
 * `transcribing`) — web-ui-issues #16. Shows a spinner, a status-specific label ("Queued" /
 * "Transcribing…"), and a live elapsed-time readout (`useElapsedLabel`, ticking from `since`)
 * instead of a fabricated percent bar — AWS Transcribe batch gives no progress signal to show
 * one honestly. `role="status"` so screen readers announce label changes (Queued ->
 * Transcribing…) without a full page announcement storm on every tick of the elapsed timer.
 * Label and elapsed time are each their own `<span>` (not bare text) so they're independently
 * targetable in tests/assistive tech rather than concatenated into one opaque text node. */
export function InProgressStatus({
  phase,
  since,
}: {
  phase: InProgressPhase;
  since: string | null | undefined;
}) {
  const elapsed = useElapsedLabel(since, true);
  return (
    <span className="status-chip status-prog inprogress-chip" role="status">
      <Spinner size={13} />
      <span className="status-label">{IN_PROGRESS_STATUS_LABEL[phase] ?? 'Processing…'}</span>
      {elapsed && <span className="status-elapsed mono">{elapsed}</span>}
    </span>
  );
}

const AVATAR_HUES = 5;

/** Deterministic 0..4 brand-hue index (Savoy / Robin-700 / Plum / Midnight-700 / Blush
 * cycle) for a name/label — shared by Avatar and speaker-attributed transcript UI so the
 * same person always maps to the same on-brand hue everywhere they appear. */
export function brandHue(label: string): number {
  let hash = 0;
  for (let i = 0; i < label.length; i++) hash = (hash * 31 + label.charCodeAt(i)) >>> 0;
  return hash % AVATAR_HUES;
}

/* Brand-hue dot per folder (same 5-hue cycle as Avatar/speakerColor) so each folder gets a
 * stable, on-brand color swatch wherever it's listed — FoldersScreen's tree and the
 * AppShell rail's Folders section (SHARE-5) both consume this single definition rather
 * than forking it. */
const FOLDER_DOT_VARS = [
  'var(--savoy)',
  'var(--robin-700)',
  'var(--plum)',
  'var(--navy-700)',
  'var(--blush-accessible)',
];

export function folderDotColor(id: string): string {
  return FOLDER_DOT_VARS[brandHue(id)]!;
}

/** Deterministic brand-hue avatar (Savoy / Robin-700 / Plum / Midnight-700 / Blush cycle)
 * for a person's initials, so the same person always renders the same color. */
export function Avatar({
  name,
  size = 'md',
  className = '',
}: {
  name: string;
  size?: 'sm' | 'md';
  className?: string;
}) {
  const hue = brandHue(name);
  const initials = name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join('');
  return (
    <span
      className={`avatar hue-${hue}${size === 'sm' ? ' sm' : ''} ${className}`}
      title={name}
      aria-hidden="true"
    >
      {initials || '?'}
    </span>
  );
}

export function AvatarStack({ names }: { names: string[] }) {
  return (
    <div className="avatar-stack" role="group" aria-label={`${names.length} participants`}>
      {names.map((n, i) => (
        <Avatar key={n + i} name={n} size="sm" />
      ))}
    </div>
  );
}

/** Pill search input matching the mockup's center-column search bar. The optional
 * `onClick`/`inputRef`/`expanded`/`controlsId` props exist for callers that pair this with a
 * results overlay (see AppShell's global ⌘K search) — omit them for a plain filter input like
 * RecordingsList's and behavior is unchanged. `onClick` (not `onFocus`) is deliberate: a
 * programmatic `.focus()` call — e.g. the shared focus-trap restoring focus once the overlay
 * closes — never dispatches a click, so wiring "open" to a real click/keydown rather than
 * focus avoids the overlay silently reopening itself right after it closes. */
export function SearchInput({
  value,
  onChange,
  placeholder,
  ariaLabel,
  shortcut,
  onClick,
  inputRef,
  expanded,
  controlsId,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  ariaLabel?: string;
  shortcut?: string;
  onClick?: () => void;
  inputRef?: Ref<HTMLInputElement>;
  expanded?: boolean;
  controlsId?: string;
}) {
  return (
    <div className="search-input">
      <MagnifyingGlassIcon width={20} height={20} aria-hidden="true" />
      <input
        ref={inputRef}
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onClick={onClick}
        placeholder={placeholder}
        aria-label={ariaLabel ?? placeholder}
        {...(controlsId
          ? { role: 'combobox', 'aria-expanded': !!expanded, 'aria-controls': controlsId, 'aria-haspopup': 'listbox' as const }
          : {})}
      />
      {shortcut && (
        <span className="kbd" aria-hidden="true">
          {shortcut}
        </span>
      )}
    </div>
  );
}

/** Page-number pager driven by a `Paginated<T>.page_info` (see openapi/scribe.yaml). Renders
 * nothing when there's only one page of results, so it's safe to always mount. */
export function Pagination({
  pageInfo,
  onPageChange,
}: {
  pageInfo: PageInfo;
  onPageChange: (page: number) => void;
}) {
  const { page, page_size, total, has_more } = pageInfo;
  const totalPages = page_size > 0 ? Math.max(1, Math.ceil(total / page_size)) : 1;
  if (totalPages <= 1 && !has_more) return null;

  const from = total === 0 ? 0 : (page - 1) * page_size + 1;
  const to = Math.min(page * page_size, total);

  return (
    <div className="pagination">
      <span className="pagination-summary">
        {total === 0 ? 'No results' : `${from}–${to} of ${total}`}
      </span>
      <div className="pagination-controls">
        <button
          type="button"
          className="ghost sm"
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
        >
          Previous
        </button>
        <span className="pagination-page">
          Page {page}
          {totalPages > 1 ? ` of ${totalPages}` : ''}
        </span>
        <button
          type="button"
          className="ghost sm"
          disabled={!has_more}
          onClick={() => onPageChange(page + 1)}
        >
          Next
        </button>
      </div>
    </div>
  );
}

const STATUS_KIND: Record<RecordingStatus, string> = {
  uploaded: 'neutral',
  queued: 'pending',
  transcribing: 'info',
  completed: 'success',
  failed: 'danger',
};

export function StatusBadge({ status }: { status: RecordingStatus }) {
  return <Badge kind={STATUS_KIND[status] ?? 'neutral'}>{status}</Badge>;
}

/** Playback-speed picker (SPEED task) — a real `<select>` (native, so it's keyboard/screen
 * reader accessible for free) shared by TranscriptPlayer's synced player and
 * RecordingDetail's standalone pre-transcript `AudioSection`. Presentational only: callers own
 * the persisted rate via `usePlaybackRate` (../hooks/usePlaybackRate) and pass it down. */
export function PlaybackSpeedControl({
  rate,
  onChange,
}: {
  rate: PlaybackRate;
  onChange: (rate: PlaybackRate) => void;
}) {
  return (
    <label className="playback-speed-control">
      <span className="playback-speed-control-label">Speed</span>
      <select
        aria-label="Playback speed"
        value={rate}
        onChange={(e) => onChange(Number(e.target.value) as PlaybackRate)}
      >
        {PLAYBACK_RATES.map((r) => (
          <option key={r} value={r}>
            {r}x
          </option>
        ))}
      </select>
    </label>
  );
}

/** mm:ss (or h:mm:ss for long recordings) from a millisecond offset. */
export function formatMs(ms: number): string {
  const totalSec = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  const mm = h > 0 ? String(m).padStart(2, '0') : String(m);
  const ss = String(s).padStart(2, '0');
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

/** `$`-prefixed USD amount for the streaming cost indicator — 2 decimals, or 4 under $0.01
 * so a session's first few seconds don't just read "$0.00". */
export function formatUsd(amount: number): string {
  return amount < 0.01 ? `$${amount.toFixed(4)}` : `$${amount.toFixed(2)}`;
}

/** mm:ss from a whole-second duration (Recording.duration_sec). */
export function formatDuration(sec: number | null | undefined): string {
  if (sec == null) return '—';
  return formatMs(sec * 1000);
}

/** ONDEMAND-1: "~12 min" from a size-based duration ESTIMATE (`estimated_duration_sec`) —
 * the tilde is the estimate marker; callers also title/label it as estimated. */
export function formatEstimatedMinutes(sec: number): string {
  return `~${Math.max(1, Math.round(sec / 60))} min`;
}

export function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

/* Text-safe (4.5:1+ on white) color per brand hue index — mirrors Avatar's fill cycle
 * (Savoy / Robin-ink / Plum / Midnight-700 / Blush-accessible) so a speaker's label color
 * in the transcript always matches their avatar fill. Uses CSS custom properties so it
 * follows the token layer in styles.css rather than hardcoding hex here.
 * Note: hue-1 uses --robin-ink (#1A5B64, 6.46:1 on white), not --robin-700 (#268E9C,
 * only 3.87:1 on white) — the latter fails WCAG AA for text despite being labeled "text"
 * in the design-system token doc; --robin-ink is the accessible choice for actual text. */
const SPEAKER_LABEL_VARS = [
  'var(--savoy-hover)',
  'var(--robin-ink)',
  'var(--plum)',
  'var(--navy-700)',
  'var(--blush-accessible)',
];

/** Deterministic on-brand color for a speaker label, so the same speaker always renders
 * the same color within a transcript — cycles the same 5 brand hues as Avatar. */
export function speakerColor(label: string): string {
  return SPEAKER_LABEL_VARS[brandHue(label)]!;
}

const SPK_LABEL_RE = /^spk_(\d+)$/;

/** Diarized `spk_<n>` label -> 1-indexed human-facing name (`spk_0` -> "Speaker 1"). Any
 * label that doesn't match the `spk_<n>` shape (a display name, or already-resolved speaker)
 * is returned unchanged. Shared by `LiveCaptions` (WEBSTREAM-3) and `web-ui-bugs`/WEBUI-2 —
 * keep this the single definition; do not fork it. */
export function formatSpeaker(label: string): string {
  const match = SPK_LABEL_RE.exec(label);
  if (!match) return label;
  return `Speaker ${Number(match[1]) + 1}`;
}

const SNIPPET_MARKER_RE = /(<b>|<\/b>)/g;

interface SnippetSegment {
  text: string;
  bold: boolean;
}

/** Splits a Postgres `ts_headline` snippet on the literal `<b>`/`</b>` marker strings it
 * wraps matches in. Only those two exact literal markers are ever treated as markup — any
 * other bracketed text (e.g. a speaker literally saying "less than b script greater than",
 * transcribed as `<script>`) is just another plain-text segment, so it can never be
 * (mis)rendered as an element. Bold depth is clamped at 0 and an unmatched `<b>` simply
 * bolds the remainder of the string, so unbalanced or nested markers degrade gracefully
 * instead of throwing or losing content. Exported for the parsing unit tests; UI code
 * should render via `SearchSnippet` below rather than call this directly. */
export function parseSnippetSegments(snippet: string): SnippetSegment[] {
  const parts = snippet.split(SNIPPET_MARKER_RE);
  const segments: SnippetSegment[] = [];
  let depth = 0;
  for (const part of parts) {
    if (part === '<b>') {
      depth += 1;
      continue;
    }
    if (part === '</b>') {
      depth = Math.max(0, depth - 1);
      continue;
    }
    if (part === '') continue;
    segments.push({ text: part, bold: depth > 0 });
  }
  return segments;
}

/** Renders a search-result snippet with its `<b>`/`</b>` match markers turned into real
 * `<strong>` elements — never `dangerouslySetInnerHTML`. Every other character (including
 * literal HTML a speaker said out loud) renders as inert text, same as any other React
 * string child. See `parseSnippetSegments` for the marker-parsing rules. */
export function SearchSnippet({ snippet }: { snippet: string }) {
  return (
    <>
      {parseSnippetSegments(snippet).map((seg, i) =>
        seg.bold ? <strong key={i}>{seg.text}</strong> : <Fragment key={i}>{seg.text}</Fragment>,
      )}
    </>
  );
}

/** Fraction (0-1) of an upload item's parts that have completed — used to drive a progress
 * bar reflecting @scribe/core's upload-queue FSM (recorded -> queued -> uploading(partN) ->
 * completing -> done). `done` is always reported as fully complete regardless of
 * completedParts bookkeeping. */
export function uploadProgressRatio(item: UploadItem): number {
  if (item.state.kind === 'done') return 1;
  const partCount = item.source.partCount || 1;
  return Math.min(1, item.completedParts.length / partCount);
}

/** Short, human-facing label for an upload item's current FSM state. */
export function uploadStateLabel(item: UploadItem): string {
  switch (item.state.kind) {
    case 'recorded':
      return 'preparing…';
    case 'queued':
      return 'queued';
    case 'uploading':
      return `uploading part ${item.state.partNumber} of ${item.source.partCount}`;
    case 'completing':
      return 'finalizing…';
    case 'done':
      return 'done';
    case 'failed':
      return item.state.retryable ? 'failed (retryable)' : 'failed';
  }
}

export function UploadProgressBar({ item }: { item: UploadItem }) {
  const ratio = uploadProgressRatio(item);
  const failed = item.state.kind === 'failed';
  return (
    <div
      className={'upload-bar' + (failed ? ' upload-bar-failed' : '')}
      role="progressbar"
      aria-valuenow={Math.round(ratio * 100)}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <div className="upload-bar-fill" style={{ width: `${Math.round(ratio * 100)}%` }} />
    </div>
  );
}
